import React, { useCallback, useEffect, useMemo, useState, Dispatch, SetStateAction, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Cart } from 'shared/types/cart';
import Button from 'components/commercetools-ui/atoms/button';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import usePath from 'helpers/hooks/usePath';
import { useCart, useAccount } from 'frontastic';
import AddressesPreview from './previews/addresses';
import DeliveryPreview from './previews/delivery';
import PaymentPreview from './previews/payment';
import ShippingPreview from './previews/shipping';
import Addresses from './sections/addresses';
import Delivery from './sections/delivery';
import Payment from './sections/payment';
import PaymentHelper from './sections/payment/helper';
import Shipping from './sections/shipping';
import { useAdyenCheckout } from '../../provider';
import { DeliveryProps } from '../../types';
import CreateAddressModal from '../create-address-modal';
import Step from '../step';

interface Props {
  onFinalStepChange: (isFinalStep: boolean) => void;
  deliveryOptions: DeliveryProps[];
  active: number;
  setActive: Dispatch<SetStateAction<number>>;
  onSubmitPayment: () => void;
}

const Steps: React.FC<Props> = ({ onFinalStepChange, deliveryOptions, active, setActive, onSubmitPayment }) => {
  const { locale } = useLocale();
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const paymentHelper = PaymentHelper();
  const { account, loggedIn } = useAccount();
  const { data } = useCart();
  const {
    paymentStatus,
    setProcessing,
    adyenCheckoutInstanceState,
    setPaymentStatus,
    processing,
    selectedPaymentMethod,
    isPaymentDataValid,
  } = useAdyenCheckout();

  const [isDeliveryError, setIsDeliveryError] = useState(false);
  const router = useRouter();

  const searchParams = useSearchParams();

  const step = +(searchParams.get('step') ?? 0);

  const { pathWithoutQuery } = usePath();

  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState('');

  const [retainComponentData, setRetainComponentData] = useState(false);

  const [resetAddressData, setResetAddressData] = useState('false');

  const [stepCheckDone, SetStepCheckDone] = useState(false);

  const goToNextStep = useCallback(() => {
    setActive(active + 1);
    //router.push(`${pathWithoutQuery}?step=${active + 1}`);
  }, [active, router, pathWithoutQuery]);

  const onSelectDeliveryOption = (selectedId: string, resetData: boolean) => {
    setSelectedDeliveryOption(selectedId);
    if (resetData) {
      setResetAddressData(selectedId);
      setRetainComponentData(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('ecode') === 'v12rej') {
      setPaymentStatus('refused');
    }
  }, [searchParams]);

  useEffect(() => {
    if (paymentStatus !== '') {
      setActive(steps.length - 1);
    }

    if (paymentStatus === 'success') {
      setActive(active + 1);
      setProcessing(true);
    }
  }, [paymentStatus]);

  useEffect(() => {
    setActive(step);
  }, [step]);

  useEffect(() => {
    const determineCheckoutStep = (data: Cart) => {
      SetStepCheckDone(true);
      const { custom, shippingAddress, billingAddress, shippingInfo } = data;

      if (!custom?.deliveryOption || custom.deliveryOption === '') {
        return 0;
      }
      if (
        custom.deliveryOption &&
        (!shippingAddress?.postalCode ||
          shippingAddress.postalCode === '' ||
          !billingAddress?.postalCode ||
          billingAddress.postalCode === '')
      ) {
        return 1;
      }

      if (custom.deliveryOption === 'HomeDelivery' && shippingAddress?.postalCode && billingAddress?.postalCode) {
        setSelectedDeliveryOption('HomeDelivery');
        if (!shippingInfo || !shippingInfo.shippingMethodId) {
          return 2;
        } else {
          return getPaymentStepIndex();
        }
      }

      if (custom.deliveryOption === 'StoreCollection' && shippingAddress?.postalCode && billingAddress?.postalCode) {
        setSelectedDeliveryOption('StoreCollection');
        return getPaymentStepIndex();
      }

      return 0;
    };

    if (localStorage.getItem('retainComponentData')) {
      setRetainComponentData(true);
      localStorage.removeItem('retainComponentData');
    }

    if (step == 0 && data && !stepCheckDone && retainComponentData) {
      const activeSpep = determineCheckoutStep(data);
      setActive(activeSpep);
    }
  }, [data]);

  const onEdit = useCallback((index: number) => setActive(index), []);

  const getPaymentStepIndex = () => {
    return data?.custom?.deliveryOption === 'StoreCollection' ? 2 : 3;
  };

  const steps = useMemo(() => {
    const baseSteps = [
      {
        label: formatCartMessage({ id: 'delivery', defaultMessage: 'Delivery' }),
        Component: (
          <Delivery
            goToNextStep={goToNextStep}
            deliveryOptions={deliveryOptions}
            retainData={retainComponentData}
            onSelectDeliveryOption={onSelectDeliveryOption}
          />
        ),
        Preview: <DeliveryPreview />,
      },
      {
        label: formatCartMessage({ id: 'addresses', defaultMessage: 'Addresses' }),
        Component: (
          <Addresses
            goToNextStep={goToNextStep}
            active={active}
            setIsDeliveryError={setIsDeliveryError}
            retainData={retainComponentData}
            selectedDeliveryOption={selectedDeliveryOption}
            resetAddressData={resetAddressData}
          />
        ),
        Preview: <AddressesPreview />,
        CTA: <CreateAddressModal />,
      },
      {
        label: formatCartMessage({ id: 'payment', defaultMessage: 'Payment' }),
        Component: (
          <Payment
            goToNextStep={goToNextStep}
            isActive={active === getPaymentStepIndex()}
            makeActive={() => {
              setActive(steps.length - 1);
            }}
          />
        ),
        Preview: <PaymentPreview />,
      },
    ];

    if (data?.custom?.deliveryOption !== 'StoreCollection') {
      baseSteps.splice(2, 0, {
        label: formatCartMessage({ id: 'shipping', defaultMessage: 'Shipping' }),
        Component: (
          <Shipping retainData={retainComponentData} goToNextStep={goToNextStep} isDeliveryError={isDeliveryError} />
        ),
        Preview: <ShippingPreview />,
      });
    }

    return baseSteps;
  }, [formatCartMessage, goToNextStep, data?.custom?.deliveryOption]);

  const isFinalStep = useMemo(() => active === steps.length, [active, steps.length]);

  useEffect(() => {
    onFinalStepChange(isFinalStep);
  }, [isFinalStep, onFinalStepChange]);

  const hasInitializedDataLayer = useRef(false);
  useEffect(() => {
    const currentUrl = document.location.origin;
    if (!hasInitializedDataLayer.current && isFinalStep) {
      const salutation = loggedIn ? account?.salutation || '' : '';
      const paymentMethod = adyenCheckoutInstanceState?.data?.paymentMethod?.brand || '';
      const dataLayer = new DataLayerHelpers(
        currentUrl,
        locale,
        data as Record<string, any>,
        account?.email,
        account?.accountId,
        salutation,
        paymentMethod,
      );
      const dataLayerData = {
        breadcrumb: 'Checkout / Summary',
        event: 'checkout',
        step: 4,
        option: paymentMethod,
        store: data?.shippingAddress?.streetName || '',
      };

      dataLayer.setCartDataLayer(dataLayerData);
      hasInitializedDataLayer.current = true;
    }
  }, [isFinalStep]);

  return (
    <div className="px-16 md:px-24 lg:w-[70%] lg:grow lg:px-0">
      <div className="flex flex-col gap-24 lg:bg-neutral-200">
        {steps.map(({ Component, Preview, label, CTA }, index) => (
          <Step
            key={index}
            label={label}
            number={index + 1}
            isExpanded={index === active}
            isCompleted={index < active}
            onEdit={() => onEdit(index)}
            Component={Component}
            Preview={Preview}
            CTA={CTA}
          />
        ))}
      </div>
      {selectedPaymentMethod !== 'paypal' && paymentStatus !== '3ds2' && isFinalStep && (
        <div className="mt-24 lg:hidden">
          <Button
            variant="primary"
            className={`w-full uppercase ${selectedPaymentMethod !== 'paypal' ? 'block' : 'none'}`}
            type="submit"
            onClick={onSubmitPayment}
            loading={processing}
            disabled={!isFinalStep}
            size="m"
          >
            {formatCheckoutMessage({ id: 'complete.purchase', defaultMessage: 'Complete purchase' })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Steps;
